import React, { useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { motion } from "framer-motion";
import OpticalDetailsTable from "./LeftOpticalDetails";
import MultiSelect from "components/common/MultiSelect";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import BootCheckbox from "components/common/custom/forms/BootCheckbox";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

const OpticalDetails = ({ control, watch, setValue, touchedFields }) => {
  const { preauthorizationRequest, viewPreauthorizationInfo, stepFormData } =
    useSelector((state) => state.preauthorization);

  const [searchParams] = useSearchParams();
  const reuse = searchParams.get("reuse") === "true";
  const followUp = searchParams.get("followup") === "true";
  const edit = searchParams.get("edit") === "true";

  // Safely access nested fields with fallbacks

  const preauthOptical =
    viewPreauthorizationInfo?.provider_request?.opticals || [];

  const regularLenses =
    viewPreauthorizationInfo?.other_fields?.vision_extraflds?.regular_lenses ||
    "";
  const regularContact =
    viewPreauthorizationInfo?.other_fields?.vision_extraflds?.regular_contact ||
    "";
  const isFrame =
    viewPreauthorizationInfo?.other_fields?.vision_extraflds?.isframe || false;
  const lensSpecification =
    viewPreauthorizationInfo?.other_fields?.vision_extraflds
      ?.lens_specifications || [];

  const productType = watch("productType");
  const type = preauthOptical?.[0]?.type;

  useEffect(() => {
    if (!reuse && !followUp && !edit) return;

    // Helper function to set value only if field is untouched and unset
    const setIfUntouched = (fieldName, value, defaultValue = "") => {
      if (!touchedFields[fieldName] && watch(fieldName) == null) {
        setValue(fieldName, value || defaultValue);
      }
    };

    console.log({ test: "test" });

    // Set top-level values
    setIfUntouched("productType", type);
    setIfUntouched(
      "optical_date",
      viewPreauthorizationInfo?.provider_request?.opticalsdte
    );
    setIfUntouched("regularLenses", regularLenses);
    setIfUntouched("regular_contact", regularContact);
    setIfUntouched("is_frame", isFrame, false);

    // Set lens specifications (multiselect)
    if (
      Array.isArray(lensSpecification) &&
      !touchedFields.lens_specifications
    ) {
      const formattedSpecs = lensSpecification.map((spec) => ({
        value: spec.toLowerCase(),
        label: spec
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      }));
      setIfUntouched("lens_specifications", formattedSpecs, []);
    }

    if (preauthOptical?.length) {
      preauthOptical?.forEach((spec, index) => {
        const {
          eye,
          sphere,
          cylinder,
          axis,
          prism,
          add,
          color,
          brand,
          power,
          backCurve,
          diameter,
          duration,
        } = spec || {};

        // Assign default side if eye is missing
        const side = eye?.toLowerCase() || (index === 0 ? "right" : "left");

        const noteType = spec?.notes?.[0]?.text;
        const isContact = type === "contact";

        if (side) {
          // Common fields
          if (sphere) {
            const value = parseFloat(sphere).toFixed(2);
            setIfUntouched(
              `${side}_${isContact ? "power" : "sph"}_${noteType}`,
              value
            );
          }
          if (cylinder) {
            setIfUntouched(
              `${side}_cyl_${noteType}`,
              parseFloat(cylinder).toFixed(2)
            );
          }
          if (axis) {
            setIfUntouched(
              `${side}_axis_${noteType}`,
              parseInt(axis).toString()
            );
          }

          // Contact-specific
          if (isContact) {
            if (power) {
              setIfUntouched(
                `${side}_power_${noteType}`,
                parseFloat(power).toFixed(2)
              );
            }
            if (backCurve) {
              setIfUntouched(
                `${side}_backcurve_${noteType}`,
                parseFloat(backCurve)
              );
            }
            if (diameter) {
              setIfUntouched(
                `${side}_diameter_${noteType}`,
                parseFloat(diameter)
              );
            }
            if (duration) {
              setIfUntouched(`${side}_duration_value`, duration);
              setIfUntouched(`${side}_duration_unit`, "d");
            }
          }

          // Prism (only for lens)
          if (!isContact && prism?.length) {
            prism.forEach((p) => {
              if (p.base && p.amount) {
                const baseKey = p.base.toLowerCase();
                setIfUntouched(
                  `${side}_${baseKey}_${noteType}`,
                  parseFloat(p.amount).toFixed(2)
                );
              }
            });
          }
        }

        if (add) {
          setIfUntouched(`${side}_add`, parseFloat(add).toFixed(2));
        }
        if (color) {
          setIfUntouched(`${side}_color`, color);
        }
        if (brand) {
          setIfUntouched(`${side}_brand`, brand);
        }
      });
    }
  }, [reuse, followUp, edit, preauthOptical]);



  const handleReset = () => {
    // Define all optical fields to reset (excluding productType and optical_date)
    const fieldsToReset = [
      // Regular lenses/contact fields
      "regularLenses",
      "regular_contact",
      "is_frame",
      "lens_specifications",

      // Left eye - distance
      "left_sph_distance",
      "left_cyl_distance",
      "left_axis_distance",
      "left_power_distance",
      "left_backcurve_distance",
      "left_diameter_distance",
      "left_in_distance",
      "left_out_distance",
      "left_up_distance",
      "left_down_distance",

      // Left eye - near
      "left_sph_near",
      "left_cyl_near",
      "left_axis_near",
      "left_power_near",
      "left_backcurve_near",
      "left_diameter_near",
      "left_in_near",
      "left_out_near",
      "left_up_near",
      "left_down_near",

      // Right eye - distance
      "right_sph_distance",
      "right_cyl_distance",
      "right_axis_distance",
      "right_power_distance",
      "right_backcurve_distance",
      "right_diameter_distance",
      "right_in_distance",
      "right_out_distance",
      "right_up_distance",
      "right_down_distance",

      // Right eye - near
      "right_sph_near",
      "right_cyl_near",
      "right_axis_near",
      "right_power_near",
      "right_backcurve_near",
      "right_diameter_near",
      "right_in_near",
      "right_out_near",
      "right_up_near",
      "right_down_near",

      // Common fields for both eyes
      "left_add",
      "left_color",
      "left_brand",
      "left_duration_value",
      "left_duration_unit",
      "right_add",
      "right_color",
      "right_brand",
      "right_duration_value",
      "right_duration_unit",
    ];

    // Reset all fields to empty/default values
    fieldsToReset.forEach((field) => {
      if (field === "is_frame") {
        setValue(field, false);
      } else if (field === "lens_specifications") {
        setValue(field, []);
      } else {
        setValue(field, "");
      }
    });
  };

  return (
    <Row>
      {/* Product Type */}
      <Col xs={12} md={4}>
        <label className="form-label">Product Type</label>
        <Controller
          name="productType"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              className="fs-10"
              size="sm"
              onChange={(e) => {
                field.onChange(e); // Keep the original field onChange
                setValue("productType", e.target.value);
                handleReset();
              }}
            >
              <option value="">Select Product Type</option>
              <option value="lens">Lens</option>
              <option value="contact">Contact</option>
            </Form.Select>
          )}
        />
      </Col>

      {/* Conditional Fields */}
      {productType && (
        <>
          <Col xs={12} md={4}>
            <BootDatePicker
              name="optical_date"
              label="Prescription Date"
              size="sm"
              control={control}
              rules={{ required: productType }}
              selected={watch("optical_date")}
              onChange={(newValue) => setValue("optical_date", newValue)}
              placeholder="dd/mm/yyyy"
              maxDate={new Date()}
              isRequired={productType}
            />
          </Col>
          {/* Regular Lenses - Shown for both lens and contact */}
          <Col xs={12} md={4}>
            {productType === "lens" && (
              <Row>
                <Col xs={9}>
                  <label className="form-label">Regular Lenses</label>
                  <Controller
                    name="regularLenses"
                    control={control}
                    render={({ field }) => (
                      <Form.Select {...field} className="fs-10" size="sm">
                        <option value="">Select Lens Type</option>
                        <option value="glass">Glass</option>
                        <option value="plastic">Plastic</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    )}
                  />
                </Col>
                <Col xs={3}>
                  <BootCheckbox
                    name="is_frame"
                    label="Is Frame?"
                    control={control}
                    size="lg"
                  />
                </Col>
              </Row>
            )}
            {productType === "contact" && (
              <>
                <label className="form-label">Regular Contact</label>
                <Controller
                  name="regular_contact"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field} className="fs-10" size="sm">
                      <option value="">Select Contact Type</option>
                      <option value="disposable">Disposable</option>
                      <option value="permanent">Permanent</option>
                    </Form.Select>
                  )}
                />
              </>
            )}
          </Col>
          {/* Lens Specifications - Only for lens */}
          {productType === "lens" && (
            <Col xs={12}>
              <Form.Group controlId="lensType">
                <label className="form-label">Lenses Specifications</label>
                <Controller
                  name="lens_specifications"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      isMulti
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      options={[
                        "Multicoated",
                        "Multifocal",
                        "Light",
                        "Aspheric",
                        "Bifocal",
                        "Medium",
                        "Lenticular",
                        "AntiScratch",
                        "Dark",
                        "SafetyThickness",
                        "AntiReflectingCoating",
                        "PhotoSensitive",
                        "HighIndex",
                        "Colored",
                        "SingleVision",
                        "Varilux",
                      ].map((opt) => ({
                        value: opt.toLowerCase(),
                        label: opt,
                      }))}
                      isClearable
                      placeholder="Select Lens Specifications..."
                      value={field.value || []}
                      onChange={(selected) => {
                        setValue("lens_specifications", selected || []);
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          )}
        </>
      )}
      {(productType === "lens" || productType === "contact") && (
        <motion.div
          style={{ border: "2px solid #2ca271" }}
          className="rounded p-3 position-relative mt-4"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="position-absolute top-0 start-45 translate-middle-y px-2">
            <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
              <span className="fs-10">{productType}</span>
            </span>
          </div>
          <Row>
            <Col md={12}>
              <Row className="mt-1">
                <Col>
                  <OpticalDetailsTable
                    setValue={setValue}
                    control={control}
                    watch={watch}
                    productType={productType}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </motion.div>
      )}
    </Row>
  );
};

export default OpticalDetails;
